/**
 * Local (email / password) AuthProvider implementation.
 *
 * Tokens are obtained from the backend's /auth/login endpoint and
 * stored in localStorage.  Refresh is handled transparently.
 */

import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import { useImpersonation } from './useImpersonation';
import { User, AuthContextType } from './types';
import { fetchDatabaseAdminFlag } from './backendAuthorization';
import PostHogService from '../../posthog';

const ACCESS_TOKEN_KEY = 'kolega_access_token';
const REFRESH_TOKEN_KEY = 'kolega_refresh_token';

interface JwtPayload {
  sub: string;
  email: string;
  username?: string;
  name?: string;
  email_verified?: boolean;
  exp: number;
}

function userFromToken(token: string): User | null {
  try {
    const claims = jwtDecode<JwtPayload>(token);
    return {
      id: claims.sub,
      email: claims.email,
      name: claims.name || '',
      username: claims.username || claims.email.split('@')[0],
      is_admin: false,
    };
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

interface Props {
  children: ReactNode;
  context: React.Context<AuthContextType | undefined>;
}

export const LocalAuthProvider: React.FC<Props> = ({ children, context }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const hydrateUserWithDatabaseAuthorization = useCallback(
    async (accessToken: string, mappedUser: User | null): Promise<User | null> => {
      if (!mappedUser) {
        return null;
      }
      const databaseAdminFlag = await fetchDatabaseAdminFlag(accessToken);
      return {
        ...mappedUser,
        is_admin: databaseAdminFlag ?? false,
      };
    },
    [],
  );

  // Bootstrap from localStorage on mount
  useEffect(() => {
    let isComponentMounted = true;

    const bootstrapLocalAuthState = async () => {
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token && !isTokenExpired(token)) {
        const mappedUser = userFromToken(token);
        const hydratedUser = await hydrateUserWithDatabaseAuthorization(token, mappedUser);
        if (isComponentMounted) {
          setUser(hydratedUser);
        }
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }

      if (isComponentMounted) {
        setIsLoading(false);
      }
    };

    void bootstrapLocalAuthState();
    return () => {
      isComponentMounted = false;
    };
  }, [hydrateUserWithDatabaseAuthorization]);

  const isAdmin = user?.is_admin || false;
  const {
    impersonationContext,
    startImpersonation,
    stopImpersonation,
    getImpersonationTimeRemaining,
  } = useImpersonation(user, isAdmin);

  // Attempt to refresh the access token silently
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;

    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setUser(null);
        return null;
      }

      const data = await response.json();
      localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      const hydratedUser = await hydrateUserWithDatabaseAuthorization(
        data.access_token,
        userFromToken(data.access_token),
      );
      setUser(hydratedUser);
      return data.access_token;
    } catch {
      return null;
    }
  }, [hydrateUserWithDatabaseAuthorization]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token && !isTokenExpired(token)) return token;
    return refreshAccessToken();
  }, [refreshAccessToken]);

  const logout = useCallback(async () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
    Sentry.setUser(null);
    PostHogService.reset();
  }, []);

  const checkAuth = useCallback(async () => user !== null, [user]);

  const isAuthenticated = user !== null;
  const displayName = user?.name || user?.username || user?.email || 'User';

  // Keep Sentry user in sync
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    }
  }, [user]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      displayName,
      logout,
      checkAuth,
      getAccessToken,
      isAdmin,
      impersonationContext,
      startImpersonation,
      stopImpersonation,
      getImpersonationTimeRemaining,
    }),
    [
      user, isAuthenticated, isLoading, displayName,
      logout, checkAuth, getAccessToken, isAdmin,
      impersonationContext, startImpersonation,
      stopImpersonation, getImpersonationTimeRemaining,
    ],
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};

/**
 * Helper: call /auth/login and persist the tokens.
 * Returns the decoded User on success.
 */
export async function localLogin(email: string, password: string): Promise<User> {
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const response = await fetch(`${apiBase}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Login failed');
  }

  const data = await response.json();
  localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);

  const user = userFromToken(data.access_token);
  if (!user) throw new Error('Failed to decode token');

  // Force a page reload so the AuthProvider picks up the new token
  window.location.href = '/repositories';
  return user;
}

/**
 * Helper: call /auth/register and persist the tokens.
 */
export async function localRegister(
  email: string,
  password: string,
  name?: string,
): Promise<User> {
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const response = await fetch(`${apiBase}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Registration failed');
  }

  const data = await response.json();
  localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);

  const user = userFromToken(data.access_token);
  if (!user) throw new Error('Failed to decode token');

  window.location.href = '/organization-setup';
  return user;
}
